<template>
    <div class="past-promotions-grid-container">
        <overview-filter
            page-section="pastPromotions"
            include-date-filter
            @change="handleSelectorChange"
        />

        <div class="quick-filter-bar">
            <!-- Note: the select already has a label so that's why not specifying a for attr on this label -->
            <label class="quick-filter-bar__label"> {{ $t('general.labels.quickFilter') }}: </label>
            <v-text-field
                v-model="quickFilterText"
                class="rtls-text-field rtls-text-field--white pb-2"
                autocomplete="off"
                :placeholder="toSentenceCase($t('placeholders.quickFilter'))"
                @input="updatePastPromoQuickFilter"
            />
        </div>

        <promo-ag-grid
            ref="pastPromoGrid"
            :row-data="rowData"
            :column-defs="columnDefs"
            :default-col-def="defaultColDef"
            :grid-options="gridOptions"
            grid-style="width: 100%; height: 100%;"
            grid-class="ag-theme-custom__parkinglot-grid"
            dom-layout="normal"
            @grid-ready="onGridReady"
        />

        <promotion-viewer-dialog
            ref="promotion-viewer-dialog"
            :key="`promotion-viewer::${selectedPromotion._id}::${promotionToggle}`"
            :promotion="selectedPromotion"
            is-past-promotions
            @close="promotionToggle = !promotionToggle"
        />

        <v-btn
            class="status-bar__action--right"
            primary
            :disabled="allocationDialogDisabled"
            @click="openAllocationDialog"
        >
            {{ toSentenceCase($t('parkingLot.allocateSelectedPromotions')) }}
        </v-btn>

        <allocate-past-promotions
            ref="promotion-allocation-dialog"
            :promotion-ids="selectedPromotionIds"
            :has-activator="false"
            is-past-promotions
            @confirm="confirmAllocatedPromotions"
        />
    </div>
</template>

<script>
import { markRaw } from 'vue';
import { mapState } from 'vuex';
import { debounce, isEmpty } from 'lodash';
import agGridUtils from '@/js/utils/ag-grid-utils';
import parkingLotGridMixin from '../mixins/parking-lot-grids-mixin.vue';

export default {
    mixins: [parkingLotGridMixin],
    data() {
        return {
            where: {},
            isPastPromotions: true,
            quickFilterText: '',
            gridOptions: markRaw({
                onSelectionChanged: this.onSelectionChanged,
                rowHeight: 35, // Specified in pixels.
                headerHeight: 47,
                rowSelection: {
                    mode: 'multiRow',
                    selectAll: 'filtered',
                    enableClickSelection: false,
                    checkboxes: false,
                    headerCheckbox: false,
                },
                statusBar: {
                    statusPanels: [
                        { statusPanel: 'agSelectedRowCountComponent', align: 'right' },
                        { statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'center' },
                    ],
                },
                columnTypes: {
                    numericColumnCustom: agGridUtils.columnTypes.numericColumnCustom,
                    filterEnabled: {
                        filter: true,
                        menuTabs: ['filterMenuTab'],
                    },
                },
            }),
        };
    },
    computed: {
        ...mapState('clientConfig', ['overviewPageConfig']),
        allocationDialogDisabled() {
            return isEmpty(this.selectedPromotionIds);
        },
    },
    created() {
        this.limit = this.overviewPageConfig.pastPromotions.maxPromotionsToLoad;
        this.identifierColumns = this.getIdentifierColumns({
            hideChannelColumns: this.overviewPageConfig.pastPromotions.hideChannelColumns,
            hideMechanicColumn: this.overviewPageConfig.pastPromotions.hideMechanicColumn,
        });
        this.detailColumns = this.getDetailsColumns({
            columnGroups: this.overviewPageConfig.pastPromotions.detailColumns,
        });

        this.columnDefs = [
            ...this.identifierColumns,
            ...this.getActionColumns({
                isPastPromotions: true,
                disableExpandPastPromotions: this.overviewPageConfig.pastPromotions
                    .disableExpandPastPromotions,
            }),
            ...this.detailColumns,
            {
                children: [
                    {
                        minWidth: 40,
                        width: 40,
                        flex: 1,
                        headerComponent: 'selectAllHeader',
                        headerClass: 'last-header',
                        cellRenderer: 'pastPromotionsCheckboxRenderer',
                        filter: 'agSetColumnFilter',
                        menuTabs: ['filterMenuTab'],
                    },
                ],
            },
        ];
    },
    methods: {
        async handleSelectorChange({ where }) {
            this.where = where;
            this.refetchPromotions();
        },

        confirmAllocatedPromotions() {
            this.selectedPromotionIds = [];
        },

        updatePastPromoQuickFilter: debounce(function() {
            this.$refs.pastPromoGrid.gridApi.setGridOption('quickFilterText', this.quickFilterText);
        }, 300),
    },
};
</script>

<style scoped lang="scss">
@import '@style/base/_variables.scss';
@import '@style/base/_mixins.scss';

.quick-filter-bar {
    @include promo-component-border($top: true);

    width: 100%;
    padding: 1rem 0 0 1rem;
    display: flex;
    background-color: $promo-table-white-bg-colour;

    &__label {
        color: $filter-bar-text-colour;
        line-height: 2.8rem;
        font-size: 1.4rem;
        font-weight: 300;
        margin-right: 1rem;
    }

    .rtls-text-field {
        max-width: 40rem;
    }
}

.past-promotions-grid-container {
    height: 100%;
    width: 100%;
    display: flex;
    flex-grow: 1;
    flex-direction: column;

    &__download-btn {
        position: absolute;
        left: 49rem;
        font-size: 1.2rem;
    }
}

:deep(.center-cell-items) {
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    justify-content: center;
}

:deep(.ag-status-bar) {
    min-height: 5rem;
}

:deep(.ag-status-bar-right) {
    margin-right: 20rem;
}

:deep(.status-bar__action--right) {
    position: absolute;
    bottom: 1.5rem;
    border-top: none;
    right: 2rem;
}

:deep(.status-bar__action--left) {
    height: 2.5rem;
    position: absolute;
    bottom: 1rem;
    border-top: none;
    left: 2rem;
    display: flex;
    align-items: center;

    .v-input__details {
        display: none;
    }
}

:deep(.promotion-rag-colour) {
    margin-top: 1rem !important;
}
</style>
